import {
  ASSET_TYPE_PREFIX,
  CODE_ALIASES,
  DESCRIPTION_ALIASES,
  IGNORED_HEADERS,
  PROPERTY_PREFIXES,
} from './constants';
import {
  IColumnMapping, IImportPlan, IPropertyColumn, IPropertyRename, TImportTarget,
} from './i-asset-type-import';

const NO_COLUMN = -1;

const normalise = (header: string): string => header.trim().toLowerCase().replace(/[\s_.-]/g, '');

const propertyNameFromPrefix = (header: string): string | null => {
  const lower = header.trim().toLowerCase();
  const prefix = PROPERTY_PREFIXES.find((candidate) => lower.startsWith(candidate));
  return prefix ? header.trim().slice(prefix.length) : null;
};

const stripAssetTypePrefix = (header: string): string => {
  const trimmed = header.trim();
  return trimmed.toLowerCase().startsWith(ASSET_TYPE_PREFIX)
    ? trimmed.slice(ASSET_TYPE_PREFIX.length)
    : trimmed;
};

const classify = (
  header: string,
  codeTaken: boolean,
  descriptionTaken: boolean,
): { target: TImportTarget; propertyName: string } => {
  const explicitProperty = propertyNameFromPrefix(header);
  if (explicitProperty !== null) {
    return { target: 'property', propertyName: explicitProperty };
  }

  const bare = stripAssetTypePrefix(header);
  const key = normalise(bare);

  if (IGNORED_HEADERS.includes(key)) {
    return { target: 'ignore', propertyName: '' };
  }
  if (!codeTaken && CODE_ALIASES.includes(key)) {
    return { target: 'code', propertyName: '' };
  }
  if (!descriptionTaken && DESCRIPTION_ALIASES.includes(key)) {
    return { target: 'description', propertyName: '' };
  }
  return { target: 'property', propertyName: header.trim() };
};

export const detectMapping = (header: string[]): IImportPlan => {
  const columns: IColumnMapping[] = [];
  const warnings: string[] = [];
  let codeColumn = NO_COLUMN;
  let descriptionColumn = NO_COLUMN;

  header.forEach((raw, columnIndex) => {
    const { target, propertyName } = classify(
      raw,
      codeColumn !== NO_COLUMN,
      descriptionColumn !== NO_COLUMN,
    );

    if (target === 'code') {
      codeColumn = columnIndex;
    }
    if (target === 'description') {
      descriptionColumn = columnIndex;
    }

    columns.push({
      columnIndex, header: raw.trim(), target, propertyName,
    });
  });

  let usedFallback = false;

  if (codeColumn === NO_COLUMN && columns.length > 0) {
    usedFallback = true;
    columns[0] = { ...columns[0], target: 'code', propertyName: '' };
    codeColumn = 0;

    if (descriptionColumn === NO_COLUMN && columns.length > 1) {
      columns[1] = { ...columns[1], target: 'description', propertyName: '' };
      descriptionColumn = 1;
    }

    warnings.push(
      'No code column was recognised. Falling back to the documented positions '
      + '(first column code, second description) — check the mapping before importing.',
    );
  } else if (descriptionColumn === NO_COLUMN) {
    warnings.push('No description column was recognised — map one before importing.');
  }

  const propertyColumns: IPropertyColumn[] = columns
    .filter((column) => column.target === 'property')
    .map((column) => ({ columnIndex: column.columnIndex, name: column.propertyName }));

  return {
    columns,
    codeColumn,
    descriptionColumn,
    propertyColumns,
    confident: !usedFallback && codeColumn !== NO_COLUMN && descriptionColumn !== NO_COLUMN,
    warnings,
    propertyRenames: [],
  };
};

const propertyColumnsOf = (columns: IColumnMapping[]): IPropertyColumn[] => columns
  .filter((column) => column.target === 'property')
  .map((column) => ({ columnIndex: column.columnIndex, name: column.propertyName }));

// Property names are free-form strings server-side, so `Color` and `color` are two different
// properties. Reuse the tenant's existing spelling rather than minting a near-duplicate.
export const alignPropertyNames = (plan: IImportPlan, existingNames: string[]): IImportPlan => {
  const bySpelling = new Map<string, string>();
  existingNames.forEach((name) => {
    const key = name.trim().toLowerCase();
    if (key !== '' && !bySpelling.has(key)) {
      bySpelling.set(key, name.trim());
    }
  });

  if (bySpelling.size === 0) {
    return plan;
  }

  const propertyRenames: IPropertyRename[] = [];

  const columns = plan.columns.map((column) => {
    if (column.target !== 'property') {
      return column;
    }
    const from = column.propertyName.trim();
    const to = bySpelling.get(from.toLowerCase());
    if (!to || to === from) {
      return column;
    }
    propertyRenames.push({ from, to });
    return { ...column, propertyName: to };
  });

  if (propertyRenames.length === 0) {
    return plan;
  }

  return {
    ...plan,
    columns,
    propertyColumns: propertyColumnsOf(columns),
    propertyRenames,
  };
};
